import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { classNames } from "../../Utilities";
import styles from "./Reorderable.module.scss";
import { startAutoScroll, stopAutoScroll } from "../../Helpers/AutoScroll";

interface ReorderableProps<T> {
    items: T[];
    itemId: (item: T) => string;
    renderItem: (item: T, index: number) => ReactNode;
    onReorder: (newOrder: string[]) => void;
    header?: ReactNode;
    footer?: ReactNode;
    containerClassName?: string;
    itemClassName?: string;
    disabled?: boolean;
}

interface SwapRef {
    from: number;
    to: number;
    direction: "down" | "up";
}

interface DragingInfo {
    draggingIndex: number;
    isDraggingDown: boolean;
    rect: DOMRect;
    clientY: number;
    threshold: number;
    reverseThreshold: number;
    lastSwapRef: SwapRef | null;
}

export const Reorderable = <T,>(props: ReorderableProps<T>) => {
    const [draggingItem, setDraggingItem] = useState<T | null>(null);
    // localItems is mutated whenever the user is dragging. props.items only changes when they drop the item.
    const [localItems, setLocalItems] = useState(props.items);
    // itemRefs is used to find each item's position
    const itemRefs = useRef(new Map<string, HTMLDivElement>());
    // prevPositions is used to animate the list
    const prevPositionsRef = useRef<Map<string, DOMRect> | null>(null);
    // lastSwapRef allows us to prevent immediate back swaps
    const lastSwapRef = useRef<SwapRef | null>(null);
    // draggingItemRef mirrors draggingItem so pointer handlers always see the latest value without stale closures
    const draggingItemRef = useRef<T | null>(null);
    // activePointerIdRef tracks the pointer currently performing the drag, so we can ignore unrelated pointers
    const activePointerIdRef = useRef<number | null>(null);

    const getPositions = () => {
        const positions = new Map<string, DOMRect>();
        localItems.forEach((item) => {
            const el = itemRefs.current.get(props.itemId(item));
            if (el) positions.set(props.itemId(item), el.getBoundingClientRect());
        });
        return positions;
    };

    const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>, item: T) => {
        if (props.disabled) return;
        // Only initiate a drag for the primary mouse button, but allow any touch/pen contact
        if (e.pointerType === "mouse" && e.button !== 0) return;

        // Don't start a drag if the pointer went down on an interactive element (e.g. a button) -
        // otherwise the pointer capture below would swallow the subsequent click event
        if (e.target instanceof Element && e.target.closest("button, a, input, select, textarea, [role='button']")) return;

        activePointerIdRef.current = e.pointerId;
        draggingItemRef.current = item;
        setDraggingItem(item);

        e.currentTarget.setPointerCapture(e.pointerId);
    };

    const endDrag = () => {
        activePointerIdRef.current = null;
        draggingItemRef.current = null;
        setDraggingItem(null);
        lastSwapRef.current = null;
        stopAutoScroll();
    };

    const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        if (activePointerIdRef.current !== e.pointerId) return;

        const currentDraggingItem = draggingItemRef.current;
        if (!currentDraggingItem) return;

        // Prevents touch devices from scrolling the page while a drag is in progress
        e.preventDefault();

        startAutoScroll(e.clientY, e.currentTarget);

        const elementUnderPointer = document.elementFromPoint(e.clientX, e.clientY);
        const hoverElement = elementUnderPointer instanceof Element
            ? elementUnderPointer.closest<HTMLElement>(`.${styles.reorderableItem}`)
            : null;
        if (!hoverElement) return;

        const hoverItem = localItems.find(i => props.itemId(i) === hoverElement.id);
        if (!hoverItem) return;

        const rect = hoverElement.getBoundingClientRect();

        const draggingIndex = localItems.indexOf(currentDraggingItem);
        const hoverIndex = localItems.indexOf(hoverItem);

        if (draggingIndex === hoverIndex) return;

        const isDraggingDown = draggingIndex < hoverIndex;

        // Gets the physical positions so that we can animate from them
        // and also detect if this is a vertical list
        prevPositionsRef.current = getPositions();
        const isVerticalList = isPureVerticalList(prevPositionsRef.current);

        let shouldSwap = false;

        if (isVerticalList) {
            // The point of the reverseThreshold is to prevent an immediate swap back as the animation is carried out
            const threshold = rect.height * 0.25;
            const reverseThreshold = rect.height * 0.75;

            shouldSwap = shouldElementsSwap({ draggingIndex, isDraggingDown, rect, clientY: e.clientY, threshold, reverseThreshold, lastSwapRef: lastSwapRef.current });
        }
        else {
            shouldSwap = true;
        }

        if (!shouldSwap) return;

        const newItems = [...localItems];
        newItems.splice(draggingIndex, 1);
        newItems.splice(hoverIndex, 0, currentDraggingItem);
        setLocalItems(newItems);

        lastSwapRef.current = {
            from: draggingIndex,
            to: hoverIndex,
            direction: isDraggingDown ? "down" : "up"
        };
    };

    const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
        if (activePointerIdRef.current !== e.pointerId) return;
        if (!draggingItemRef.current) return;

        // This prevents animations running after the drop
        prevPositionsRef.current = null;
        const ids = localItems.map(i => props.itemId(i));
        props.onReorder(ids);

        endDrag();
    };

    const handlePointerCancel = (e: React.PointerEvent<HTMLDivElement>) => {
        if (activePointerIdRef.current !== e.pointerId) return;

        // A cancelled drag (e.g. interrupted by the system) reverts to the last committed order
        prevPositionsRef.current = null;
        setLocalItems(props.items);

        endDrag();
    };

    const getDraggingClass = (thisIsTheDraggingItem: boolean): string | undefined => {
        if (thisIsTheDraggingItem) {
            return styles.currentItem;
        }
    };

    // useLayoutEffect runs after DOM updates but before painting
    useLayoutEffect(() => {
        if (!prevPositionsRef.current) return;

        // Only animate if it's a vertical list: otherwise the reverse swap blocks don't work
        const isVerticalList = isPureVerticalList(prevPositionsRef.current);
        if (!isVerticalList) return;

        const prev = prevPositionsRef.current;
        prevPositionsRef.current = null;

        localItems.forEach((item) => {
            const id = props.itemId(item);
            const el = itemRefs.current.get(id);
            const prevRect = prev.get(id);
            if (!el || !prevRect) return;

            const newRect = el.getBoundingClientRect();
            const dx = prevRect.left - newRect.left;
            const dy = prevRect.top - newRect.top;

            if (dx || dy) {
                el.style.transform = `translate(${dx}px, ${dy}px)`;
                el.style.transition = "transform 0s";

                requestAnimationFrame(() => {
                    el.style.transform = "";
                    el.style.transition = "transform 250ms ease";
                });
            }
        });
    }, [localItems]);

    useEffect(() => {
        setLocalItems(props.items);
    }, [props.items]);

    return (
        <div className={props.containerClassName}>
            {props.header}
            {localItems.map((item, index) => {
                const thisIsTheDraggingItem = draggingItem != null && props.itemId(draggingItem) === props.itemId(item);

                return (
                    <div
                        className={classNames([props.itemClassName, styles.reorderableItem, !props.disabled && styles.draggable, getDraggingClass(thisIsTheDraggingItem)])}
                        key={props.itemId(item)}
                        id={props.itemId(item)}
                        draggable={false}
                        onDragStart={e => e.preventDefault()}
                        onPointerDown={e => handlePointerDown(e, item)}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerUp}
                        onPointerCancel={handlePointerCancel}
                        ref={(el) => {
                            if (el) itemRefs.current.set(props.itemId(item), el);
                        }}
                    >
                        {props.renderItem(item, index)}
                    </div>
                );
            })}
            {props.footer}
        </div>
    );
};

const isPureVerticalList = (positions: Map<string, DOMRect>) => {
    const tolerance = 5;
    const tops: number[] = [];
    for (const rect of positions.values()) {
        const top = rect.top;

        // if we find another item on the same row, it's not a vertical list
        if (tops.some(t => Math.abs(t - top) < tolerance)) {
            return false;
        }

        tops.push(top);
    }

    return true;
};

const shouldElementsSwap = (info: DragingInfo) => {
    const {
        isDraggingDown,
        rect,
        clientY,
        threshold,
        reverseThreshold,
        lastSwapRef,
        draggingIndex
    } = info;

    let shouldSwap = false;

    if (isDraggingDown) {
        const trigger = rect.top + threshold;

        // normal trigger
        if (clientY > trigger) {
            shouldSwap = true;
        }

        // block immediate reverse
        if (lastSwapRef?.direction === "up" && lastSwapRef?.to === draggingIndex) {
            const reverseTrigger = rect.top + reverseThreshold;
            if (clientY < reverseTrigger) return false;
        }
    }
    else {
        const trigger = rect.bottom - threshold;

        if (clientY < trigger) {
            shouldSwap = true;
        }

        if (lastSwapRef?.direction === "down" && lastSwapRef?.to === draggingIndex) {
            const reverseTrigger = rect.bottom - reverseThreshold;
            if (clientY > reverseTrigger) return false;
        }
    }
    return shouldSwap;
};
